import type { Auditor, Coder } from '../types';

// Coder + auditor identities (HUB-504), ported from the prototype. Coders are the
// human coders whose work is under audit (display identity only — showing a
// coder's name never reveals which blind slot, A or B, is theirs vs the agent's).

export const AUDIT_CODERS: Coder[] = [
  { id: 'cd1', name: 'Priya Raman', initials: 'PR', hue: 205 },
  { id: 'cd2', name: 'Marcus Lee', initials: 'ML', hue: 150 },
  { id: 'cd3', name: 'Sofia Alvarez', initials: 'SA', hue: 280 },
  { id: 'cd4', name: 'Ken Tanaka', initials: 'KT', hue: 32 },
];

/**
 * A coder's display identity.
 *
 * `resolved` is the load-bearing part: it says whether `name` is a real name or
 * a stand-in built from the id. A reviewer looking at a batch has to be able to
 * tell those apart.
 */
export interface CoderIdentity extends Coder {
  /** True when `name` came from a roster rather than being derived from the id. */
  resolved: boolean;
}

/**
 * Resolves an FA coder id to something displayable.
 *
 * # What this replaces
 *
 * `coderById` returned `AUDIT_CODERS[0]` on any miss — Priya Raman, the first
 * entry in a four-name demo roster. The server sends FA's raw numeric
 * CHANGEDBY_ID (3014072, 3007190, ...), which matches no demo id, so EVERY
 * coder on every batch rendered as the same face. A batch worked by five people
 * showed "PR" five times, and the column looked like a rendering bug when it
 * was a lookup that could not fail loudly.
 *
 * Falling back to a real person is the specific error: it does not merely fail
 * to identify the coder, it identifies them as someone else.
 *
 * # Why an id makes an acceptable label
 *
 * Resolving CHANGEDBY_ID to a name needs ABC_OTO_P1.USER_TABLE, which DATA_EXT
 * cannot read. Until that grant lands the id is what we honestly have, and
 * showing it distinguishes coders from one another — which is what the column
 * is for — without asserting a name we do not know.
 */
export function coderIdentity(id: string, name?: string): CoderIdentity {
  // A name resolved by FA wins over the demo roster: it is the authoritative
  // answer for a live case, and the roster only exists for the mock source.
  const supplied = name?.trim();
  if (supplied) {
    return {
      id,
      name: supplied,
      initials: initialsFor(supplied),
      hue: hueFor(id),
      resolved: true,
    };
  }
  const known = AUDIT_CODERS.find((c) => c.id === id);
  if (known) return { ...known, resolved: true };
  return {
    id,
    // Prefixed so it cannot be mistaken for a name. The trailing digits are
    // what distinguish one coder from another at avatar size.
    name: id ? `Coder ${id}` : 'Unassigned',
    initials: initialsFor(id),
    hue: hueFor(id),
    resolved: false,
  };
}

/** Two characters that fit an avatar. The last digits of an FA id vary far more
 *  than the first, which are a shared account-range prefix. */
function initialsFor(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '—';
  // A NAME initialises by its letters; an id has none, so fall back to its
  // last two characters — the digits that actually vary between FA accounts.
  if (/[A-Za-z]/.test(trimmed)) {
    const words = trimmed.split(/[\s_.-]+/).filter(Boolean);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return trimmed.slice(0, 2).toUpperCase();
  }
  return trimmed.slice(-2).toUpperCase();
}

/**
 * A stable hue per id.
 *
 * Multiplied by a large step around the wheel rather than taken modulo 360
 * directly: consecutive FA ids differ in their last digits, so a plain modulo
 * would give adjacent coders near-identical colours and the stack would read as
 * one person.
 */
function hueFor(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) {
    h = (h * 31 + id.charCodeAt(i)) >>> 0;
  }
  return (h * 137) % 360;
}

/**
 * @deprecated Use {@link coderIdentity}, which cannot resolve an unknown id to
 * a real person. Retained only for the mock source's demo roster.
 */
export function coderById(id: string): Coder {
  return AUDIT_CODERS.find((c) => c.id === id) || AUDIT_CODERS[0];
}

export const AUDITOR: Auditor = {
  id: 'auditor',
  name: 'Dana Whitfield',
  initials: 'DW',
  hue: 265,
  title: 'Anesthesia Coding Auditor',
  credentials: 'CPC, CANPC',
  email: 'dana.whitfield@coronishealth.com',
  team: 'Coding Quality & Audit',
};
